import type {
  InterventionGroupDto,
  StaffResponse,
  TreatmentPlanDto,
  TreatmentPlanTemplateResponse,
  YearPlanResponse
} from '../api/generated';
import { isAdmittedGroup } from './bedCapacityService';
import { calculateInterventionsStaffMinutes } from './capacityService';
import { calculateWeeklyCompetenceCapacityHours } from './staffCapacityService';
import {
  buildTreatmentPlanFromTemplate,
  buildWeeksFromTemplate,
  getTemplateFirstWeekNumber
} from './treatmentPlanCloneService';

const MINUTES_PER_HOUR = 60;
const PERCENT = 100;

export interface SimulationTemplateSelection {
  templateId: string;
  courseCount: number;
  // Seats per course: every group of a rolled-out course holds this many.
  capacity: number;
}

// What a selection comes to if every course is placed and every seat filled:
// the patients it makes room for and what they would bring in.
export interface SimulationSelectionEconomics {
  patientCount: number;
  revenuePerPatient: number;
  revenue: number;
}

export interface YearPlanSimulationInput {
  yearPlan: YearPlanResponse;
  templates: readonly TreatmentPlanTemplateResponse[];
  staff: readonly StaffResponse[];
  selections: readonly SimulationTemplateSelection[];
  fromWeekNumber: number;
  toWeekNumber: number;
  // The most of every profession's working hours that may be booked in total,
  // the plans already on the year plan included.
  maxStaffLoadPercent: number;
  // The location's patient rooms, one bed each: the ceiling on admitted seats
  // in any one week. No headroom is kept — a full house is expected, only
  // overbooking is a problem (the week header's bed chip takes the same view).
  bedCount: number;
}

// What stopped a course from fitting, in which week, and by how much: a
// profession's hours, or beds for its admitted patients.
export type PlacementShortage =
  | { kind: 'staff'; weekNumber: number; professionId: string; missingHours: number }
  | { kind: 'beds'; weekNumber: number; missingBeds: number };

export interface StaffPeakLoad {
  weekNumber: number;
  professionId: string;
  loadPercent: number;
}

// One step of progress. The service states what happened; the dialog tallies
// the steps that name a template into its per-template counts.
export type SimulationLogEntry =
  | { kind: 'capacity'; weekCount: number; availableHours: number; maxStaffLoadPercent: number }
  | { kind: 'beds'; bedCount: number; peakExistingBeds: number }
  | { kind: 'existing-load'; planCount: number; plannedHours: number }
  | {
      kind: 'template';
      templateId: string;
      templateName: string;
      courseCount: number;
      capacity: number;
      patientCount: number;
      weekSpan: number;
    }
  | {
      kind: 'template-too-long';
      templateId: string;
      templateName: string;
      weekSpan: number;
      periodWeekCount: number;
    }
  | { kind: 'template-without-weeks'; templateId: string; templateName: string }
  | {
      kind: 'placed';
      templateId: string;
      templateName: string;
      courseNumber: number;
      courseCount: number;
      startWeekNumber: number;
      endWeekNumber: number;
      isShifted: boolean;
    }
  | {
      kind: 'unplaced';
      templateId: string;
      templateName: string;
      courseNumber: number;
      courseCount: number;
      shortage: PlacementShortage | null;
    }
  | { kind: 'bed-peak'; weekNumber: number; usedBeds: number; bedCount: number }
  | {
      kind: 'done';
      placedCourseCount: number;
      requestedCourseCount: number;
      peakLoad: StaffPeakLoad | null;
    };

export interface YearPlanSimulationResult {
  plans: TreatmentPlanDto[];
}

type MinutesByProfession = Map<string, number>;
type MinutesByWeek = Map<number, MinutesByProfession>;
type BedsByWeek = Map<number, number>;

// A template's demand in one of its weeks, keyed by the week's distance from
// the course's start: staff minutes per profession, and beds for the seats of
// its admitted groups.
interface DemandWeek {
  weekOffset: number;
  minutes: MinutesByProfession;
  beds: number;
}

// Everything already booked into the period, by week.
interface PeriodLoad {
  minutes: MinutesByWeek;
  beds: BedsByWeek;
}

interface TemplatePlan {
  template: TreatmentPlanTemplateResponse;
  selection: SimulationTemplateSelection;
  weekSpan: number;
  demand: DemandWeek[];
  latestStartWeekNumber: number;
}

interface CourseTask {
  templatePlan: TemplatePlan;
  courseNumber: number;
  idealStartWeekNumber: number;
}

export const isSelectionRunnable = (selection: SimulationTemplateSelection): boolean =>
  selection.courseCount > 0 && selection.capacity > 0;

// What one patient brings in for getting through a course of the template:
// every group's amount, as each group seats the course's full capacity, plus
// the completion payment. The year overview prices a rolled-out plan the same
// way (yearPlanPotentialService), so the two figures agree.
export function templateRevenuePerPatient(template: TreatmentPlanTemplateResponse): number {
  const groupsAmount = template.weeks
    .flatMap((week) => week.groups)
    .reduce((sum, group) => sum + (group.reimbursementAmount ?? 0), 0);
  return groupsAmount + (template.completionReimbursementAmount ?? 0);
}

export function selectionEconomics(
  template: TreatmentPlanTemplateResponse,
  courseCount: number,
  capacity: number
): SimulationSelectionEconomics {
  const patientCount = Math.max(courseCount, 0) * Math.max(capacity, 0);
  const revenuePerPatient = templateRevenuePerPatient(template);
  return { patientCount, revenuePerPatient, revenue: patientCount * revenuePerPatient };
}

const addMinutes = (target: MinutesByProfession, source: MinutesByProfession): void => {
  for (const [professionId, minutes] of source) {
    target.set(professionId, (target.get(professionId) ?? 0) + minutes);
  }
};

const sumMinutes = (byWeek: MinutesByWeek): number => {
  let total = 0;
  for (const byProfession of byWeek.values()) {
    for (const minutes of byProfession.values()) total += minutes;
  }
  return total;
};

// A group that has been saved carries its patients, shadow seats included; one
// that has not carries only its capacity. The larger of the two is what the
// week will have to staff.
const groupPatientCount = (group: InterventionGroupDto): number =>
  Math.max(group.assignedPatients?.length ?? 0, group.capacity ?? 0);

// A plan-week whose groups hold no interventions is not running that week, so
// its seats take no bed (computeWeekKpi's rule for the bed chip).
const hasInterventions = (groups: readonly { interventions: readonly unknown[] }[]): boolean =>
  groups.some((g) => g.interventions.length > 0);

// Beds count the admitted groups' capacity, as the week header does: a seat
// is planned, whether or not anyone has been booked into it yet.
const admittedSeats = (groups: readonly InterventionGroupDto[]): number =>
  hasInterventions(groups)
    ? groups.filter(isAdmittedGroup).reduce((sum, g) => sum + (g.capacity ?? 0), 0)
    : 0;

function collectExistingLoad(
  yearPlan: YearPlanResponse,
  weekNumbers: ReadonlySet<number>
): PeriodLoad {
  const minutes: MinutesByWeek = new Map();
  const beds: BedsByWeek = new Map();
  for (const plan of yearPlan.treatmentPlans) {
    for (const week of plan.weeks) {
      const weekNumber = week.weekNumber ?? 0;
      if (!weekNumbers.has(weekNumber)) continue;
      const weekMinutes = minutes.get(weekNumber) ?? new Map<string, number>();
      for (const group of week.groups) {
        addMinutes(
          weekMinutes,
          calculateInterventionsStaffMinutes(group.interventions, groupPatientCount(group))
        );
      }
      minutes.set(weekNumber, weekMinutes);
      beds.set(weekNumber, (beds.get(weekNumber) ?? 0) + admittedSeats(week.groups));
    }
  }
  return { minutes, beds };
}

function collectCapacity(input: YearPlanSimulationInput): {
  full: MinutesByWeek;
  plannable: MinutesByWeek;
} {
  const plannableShare = Math.min(Math.max(input.maxStaffLoadPercent, 0), PERCENT) / PERCENT;
  const full: MinutesByWeek = new Map();
  const plannable: MinutesByWeek = new Map();
  for (const week of input.yearPlan.weeks) {
    if (week.weekNumber < input.fromWeekNumber || week.weekNumber > input.toWeekNumber) continue;
    const hours = calculateWeeklyCompetenceCapacityHours(input.staff, week);
    const fullMinutes = new Map<string, number>();
    const plannableMinutes = new Map<string, number>();
    for (const [professionId, professionHours] of hours) {
      fullMinutes.set(professionId, professionHours * MINUTES_PER_HOUR);
      plannableMinutes.set(professionId, professionHours * MINUTES_PER_HOUR * plannableShare);
    }
    full.set(week.weekNumber, fullMinutes);
    plannable.set(week.weekNumber, plannableMinutes);
  }
  return { full, plannable };
}

function buildTemplateDemand(
  template: TreatmentPlanTemplateResponse,
  capacity: number
): DemandWeek[] {
  const firstWeekNumber = getTemplateFirstWeekNumber(template);
  return template.weeks.map((week) => ({
    weekOffset: week.weekNumber - firstWeekNumber,
    minutes: calculateInterventionsStaffMinutes(
      week.groups.flatMap((g) => g.interventions),
      capacity
    ),
    beds: hasInterventions(week.groups) ? week.groups.filter(isAdmittedGroup).length * capacity : 0
  }));
}

const templateWeekSpan = (template: TreatmentPlanTemplateResponse): number => {
  if (template.weeks.length === 0) return 0;
  const weekNumbers = template.weeks.map((w) => w.weekNumber);
  return Math.max(...weekNumbers) - Math.min(...weekNumbers) + 1;
};

// Spreads a template's courses evenly over the start weeks it has, so a
// period is filled across its length rather than front-loaded until staff
// run out.
function idealStartWeekNumbers(
  courseCount: number,
  firstStart: number,
  lastStart: number
): number[] {
  if (courseCount === 1) return [firstStart];
  const step = (lastStart - firstStart) / (courseCount - 1);
  return Array.from({ length: courseCount }, (_, i) => firstStart + Math.round(i * step));
}

// The start weeks to try for a course, nearest its ideal first. Later before
// earlier at the same distance: courses are placed in week order, so the
// weeks behind the ideal are the ones already fuller.
function candidateStartWeekNumbers(ideal: number, firstStart: number, lastStart: number): number[] {
  const candidates = [ideal];
  for (let distance = 1; ideal + distance <= lastStart || ideal - distance >= firstStart; distance++) {
    if (ideal + distance <= lastStart) candidates.push(ideal + distance);
    if (ideal - distance >= firstStart) candidates.push(ideal - distance);
  }
  return candidates;
}

interface PeriodCapacity {
  plannable: MinutesByWeek;
  bedCount: number;
}

// The first shortage a course starting in `startWeekNumber` would run into —
// beds first, then each profession's hours — or null when it fits.
function findShortage(
  demand: readonly DemandWeek[],
  startWeekNumber: number,
  used: PeriodLoad,
  capacity: PeriodCapacity
): PlacementShortage | null {
  for (const { weekOffset, minutes, beds } of demand) {
    const weekNumber = startWeekNumber + weekOffset;
    const freeBeds = capacity.bedCount - (used.beds.get(weekNumber) ?? 0);
    // A week the course takes no beds in fits whatever the ward looks like,
    // even one the plans already on it have overbooked.
    if (beds > 0 && beds > freeBeds) {
      return { kind: 'beds', weekNumber, missingBeds: beds - Math.max(freeBeds, 0) };
    }
    const weekUsed = used.minutes.get(weekNumber);
    const weekCapacity = capacity.plannable.get(weekNumber);
    for (const [professionId, demandMinutes] of minutes) {
      if (demandMinutes === 0) continue;
      const available = (weekCapacity?.get(professionId) ?? 0) - (weekUsed?.get(professionId) ?? 0);
      if (demandMinutes > available) {
        return {
          kind: 'staff',
          weekNumber,
          professionId,
          missingHours: (demandMinutes - Math.max(available, 0)) / MINUTES_PER_HOUR
        };
      }
    }
  }
  return null;
}

function bookDemand(demand: readonly DemandWeek[], startWeekNumber: number, used: PeriodLoad): void {
  for (const { weekOffset, minutes, beds } of demand) {
    const weekNumber = startWeekNumber + weekOffset;
    const weekUsed = used.minutes.get(weekNumber) ?? new Map<string, number>();
    addMinutes(weekUsed, minutes);
    used.minutes.set(weekNumber, weekUsed);
    used.beds.set(weekNumber, (used.beds.get(weekNumber) ?? 0) + beds);
  }
}

// The fullest week's beds, or null when nothing in the period takes a bed.
function findPeakBeds(beds: BedsByWeek): { weekNumber: number; usedBeds: number } | null {
  let peak: { weekNumber: number; usedBeds: number } | null = null;
  for (const [weekNumber, usedBeds] of beds) {
    if (usedBeds > 0 && (!peak || usedBeds > peak.usedBeds)) peak = { weekNumber, usedBeds };
  }
  return peak;
}

// The busiest profession-week, measured against the staff's full hours so it
// reads on the same scale as the max staff load it was planned under.
function findPeakLoad(used: MinutesByWeek, full: MinutesByWeek): StaffPeakLoad | null {
  let peak: StaffPeakLoad | null = null;
  for (const [weekNumber, byProfession] of used) {
    for (const [professionId, minutes] of byProfession) {
      const capacity = full.get(weekNumber)?.get(professionId) ?? 0;
      if (capacity <= 0 || minutes <= 0) continue;
      const loadPercent = (minutes / capacity) * PERCENT;
      if (!peak || loadPercent > peak.loadPercent) peak = { weekNumber, professionId, loadPercent };
    }
  }
  return peak;
}

// Plans the selected treatment plans into the period, a course at a time. Each
// course is placed at the start week nearest its evenly spread ideal where
// every one of its weeks still has the staff hours and the beds it needs,
// after the plans already on the year plan and the courses placed before it
// have taken theirs. A course that fits nowhere is left out and reported, never forced
// in.
//
// Staff hours are pooled per profession and week, on the same terms as the
// week header's capacity figures, and beds are the admitted groups' seats
// against the location's rooms, as the header's bed chip counts them — so a
// proposal that passes here does not light the header up as overbooked. Room
// needs are not matched: a seat nobody is booked into has none. Which person takes which session, and on
// what day, is left to the week solver.
//
// A generator, so the caller can step through it and show progress: each
// yield is one line of the log, and the return value is the proposal.
export function* runYearPlanSimulation(
  input: YearPlanSimulationInput
): Generator<SimulationLogEntry, YearPlanSimulationResult> {
  const { full, plannable } = collectCapacity(input);
  const periodWeekNumbers = new Set(full.keys());

  yield {
    kind: 'capacity',
    weekCount: periodWeekNumbers.size,
    availableHours: sumMinutes(plannable) / MINUTES_PER_HOUR,
    maxStaffLoadPercent: input.maxStaffLoadPercent
  };

  const used = collectExistingLoad(input.yearPlan, periodWeekNumbers);
  const periodCapacity: PeriodCapacity = { plannable, bedCount: input.bedCount };
  yield {
    kind: 'beds',
    bedCount: input.bedCount,
    peakExistingBeds: findPeakBeds(used.beds)?.usedBeds ?? 0
  };
  yield {
    kind: 'existing-load',
    planCount: input.yearPlan.treatmentPlans.filter((p) =>
      p.weeks.some((w) => periodWeekNumbers.has(w.weekNumber ?? 0))
    ).length,
    plannedHours: sumMinutes(used.minutes) / MINUTES_PER_HOUR
  };

  const templatePlans: TemplatePlan[] = [];
  for (const selection of input.selections) {
    const template = input.templates.find((t) => t.id === selection.templateId);
    if (!template || !isSelectionRunnable(selection)) continue;

    const weekSpan = templateWeekSpan(template);
    if (weekSpan === 0) {
      yield { kind: 'template-without-weeks', templateId: template.id, templateName: template.name };
      continue;
    }
    const latestStartWeekNumber = input.toWeekNumber - weekSpan + 1;
    if (latestStartWeekNumber < input.fromWeekNumber) {
      yield {
        kind: 'template-too-long',
        templateId: template.id,
        templateName: template.name,
        weekSpan,
        periodWeekCount: periodWeekNumbers.size
      };
      continue;
    }

    templatePlans.push({
      template,
      selection,
      weekSpan,
      demand: buildTemplateDemand(template, selection.capacity),
      latestStartWeekNumber
    });
    yield {
      kind: 'template',
      templateId: template.id,
      templateName: template.name,
      courseCount: selection.courseCount,
      capacity: selection.capacity,
      patientCount: selection.courseCount * selection.capacity,
      weekSpan
    };
  }

  // Week order across all templates, so no template is given the whole
  // period's staff before the next one gets a look in.
  const tasks: CourseTask[] = templatePlans.flatMap((templatePlan) =>
    idealStartWeekNumbers(
      templatePlan.selection.courseCount,
      input.fromWeekNumber,
      templatePlan.latestStartWeekNumber
    ).map((idealStartWeekNumber, i) => ({
      templatePlan,
      courseNumber: i + 1,
      idealStartWeekNumber
    }))
  );
  tasks.sort((a, b) => a.idealStartWeekNumber - b.idealStartWeekNumber);

  const plans: TreatmentPlanDto[] = [];

  for (const { templatePlan, courseNumber, idealStartWeekNumber } of tasks) {
    const { template, selection, demand } = templatePlan;
    const { courseCount } = selection;
    const startWeekNumber = candidateStartWeekNumbers(
      idealStartWeekNumber,
      input.fromWeekNumber,
      templatePlan.latestStartWeekNumber
    ).find((start) => findShortage(demand, start, used, periodCapacity) === null);

    if (startWeekNumber === undefined) {
      yield {
        kind: 'unplaced',
        templateId: template.id,
        templateName: template.name,
        courseNumber,
        courseCount,
        shortage: findShortage(demand, idealStartWeekNumber, used, periodCapacity)
      };
      continue;
    }

    bookDemand(demand, startWeekNumber, used);
    const weekOffset = startWeekNumber - getTemplateFirstWeekNumber(template);
    plans.push(
      buildTreatmentPlanFromTemplate(
        template,
        buildWeeksFromTemplate(template, weekOffset, selection.capacity)
      )
    );
    yield {
      kind: 'placed',
      templateId: template.id,
      templateName: template.name,
      courseNumber,
      courseCount,
      startWeekNumber,
      endWeekNumber: startWeekNumber + templatePlan.weekSpan - 1,
      isShifted: startWeekNumber !== idealStartWeekNumber
    };
  }

  const peakBeds = findPeakBeds(used.beds);
  if (peakBeds) yield { kind: 'bed-peak', ...peakBeds, bedCount: input.bedCount };

  yield {
    kind: 'done',
    placedCourseCount: plans.length,
    requestedCourseCount: tasks.length,
    peakLoad: findPeakLoad(used.minutes, full)
  };

  return { plans };
}
